import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  CommentCreateInput,
  CreateCommentMutationMutation,
  CreateCommentMutationMutationVariables,
  CreateInventoryAdjustmentInput,
  CreateInventoryAdjustmentMutation,
  CreateInventoryAdjustmentMutationVariables,
} from "@/graphql/codegen/graphql"
import {
  CommentParentTableType,
  CreateCommentMutationDocument,
  CreateInventoryAdjustmentDocument,
  FieldGroupEnum,
  ReferenceTableName,
} from "@/graphql/codegen/graphql"
import { useFileUpload } from "@/hooks/useFileUpload"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface CreateInventoryAdjustmentHookInput extends CreateInventoryAdjustmentInput {
  comment?: string
  document?: File
}

interface UseCreateInventoryAdjustmentReturn {
  createInventoryAdjustment: (input: CreateInventoryAdjustmentHookInput) => Promise<boolean>
  isCreating: boolean
}

export const useCreateInventoryAdjustment = (): UseCreateInventoryAdjustmentReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { uploadFile } = useFileUpload()

  const [{ fetching }, executeMutation] = useMutation<
    CreateInventoryAdjustmentMutation,
    CreateInventoryAdjustmentMutationVariables
  >(CreateInventoryAdjustmentDocument)

  const [{ fetching: isCreatingComment }, executeCreateComment] = useMutation<
    CreateCommentMutationMutation,
    CreateCommentMutationMutationVariables
  >(CreateCommentMutationDocument)

  const createInventoryAdjustment = useCallback(
    async ({ comment, document, ...input }: CreateInventoryAdjustmentHookInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.inventoryAdjustment.create

      if (error || output?.__typename === "CreateInventoryAdjustmentFailure") {
        const message =
          output?.__typename === "CreateInventoryAdjustmentFailure" ? output.error.message : error?.message
        notify({
          type: NotificationType.Error,
          message: message ?? "Failed to create inventory adjustment. Please try again.",
        })
        return false
      }

      if (output?.__typename === "CreateInventoryAdjustmentSuccess") {
        const adjustmentId = output.inventoryAdjustment.inventoryAdjustmentId

        if (comment?.trim()) {
          const commentInput: CommentCreateInput = {
            value: comment,
            referenceId: adjustmentId,
            type: CommentParentTableType.InventoryAdjustment,
            isActive: true,
          }

          const { error: commentError } = await executeCreateComment({ input: commentInput })

          if (commentError) {
            notify({
              type: NotificationType.Error,
              message: commentError.message,
            })
            return false
          }
        }

        if (document) {
          const uploadSuccess = await uploadFile({
            file: document,
            type: FieldGroupEnum.Miscellaneous,
            metadata: {
              referenceId: adjustmentId,
              referenceTableName: ReferenceTableName.InventoryAdjustment,
              fieldGroup: FieldGroupEnum.Miscellaneous,
            },
            label: document.name,
          })

          if (!uploadSuccess) {
            notify({
              type: NotificationType.Error,
              message: "Inventory adjustment created but failed to upload document. Please try again.",
            })
            return false
          }
        }
      }

      notify({ type: NotificationType.Success, message: "Inventory adjustment created" })
      return true
    },
    [executeMutation, executeCreateComment, uploadFile, notify]
  )

  return { createInventoryAdjustment, isCreating: fetching || isCreatingComment }
}
